import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewEncapsulation,
  OnChanges,
  ElementRef,
  ViewChild,
  AfterViewInit,
  SimpleChange,
  OnInit,
  Renderer2
} from '@angular/core';
import { Form, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { WindowService } from 'src/shared/providers/window.service';
import { LoadingStateService } from '../../../providers/loading-state/loading-state.service';
import { ValueAccessorBase } from '../default-class/value-accessor';
import { InputMaskDataModel } from './input-mask-data.model';

let nextTidyInputId = 0;

@Component({
    selector: 'tidy-input',
    templateUrl: 'input.component.html',
    styleUrls: ['./input.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: InputComponent,
            multi: true,
        }],
    standalone: false
})

export class InputComponent extends ValueAccessorBase<any> implements OnChanges, AfterViewInit, OnInit {
  @ViewChild('maskedInput', {static: false}) maskedInputRef: ElementRef;
  // Mean normal input
  @ViewChild('neurotypicalInput', {static: false}) neurotypicalInput: ElementRef;

  @Input() public disableElement: boolean;
  @Input() public label: string;
  @Input() public placeholder: string;
  @Input() public helperText: string;
  @Input() public icon: string;
  @Input() public activeIcon: string;
  @Input() public form: FormControl;
  @Input() public formControlName: any;
  @Input() public formControl: any;
  @Input() public handleUpdatesOnKeyUp = false;
  @Input() public mask: string;
  @Input() public currencySymbol: string;
  @Input() public submitted: boolean;
  @Input() public errorMessage: string;
  @Input() public type: string;
  @Input() public noBorder: boolean;
  @Input() public inputMode: string;
  @Input() autoComplete: string;
  @Input() customMask: InputMaskDataModel;
  @Input() unmask: boolean;
  @Input() action = null;
  @Input() inputId: string;
  @Input() textSuffix: string;
  @Input() buttonIcon: string;
  @Input() buttonIconColor = 'white';
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() blur = new EventEmitter<void>();
  actionLoading: boolean;
  showSideButton = false;

  focused = false;
  touchedState = false;
  // Stable per-instance id so each input has a unique, valid id even when no
  // inputId is supplied by the caller (which is the case for every current usage).
  private readonly generatedInputId = `tidy-input-${nextTidyInputId++}`;

  // Resolved id used on the native <input>; falls back to the generated id.
  get resolvedInputId(): string {
    return this.inputId || this.generatedInputId;
  }

  // Id for the visible label element, used to wire up aria-labelledby so screen
  // readers announce the field's name.
  get labelId(): string {
    return `${this.resolvedInputId}-label`;
  }
  inputMask: InputMaskDataModel;
  showCurrencyCharacter = false;
  @Input() isInputEmpty = true;

  constructor(
    private el: ElementRef,
    public loadingState: LoadingStateService,
    private renderer: Renderer2,
    private windowService: WindowService
  ) {
    super();
  }

  ngOnInit() {
    const isSecondaryLayer = localStorage.getItem('isPrimaryLayer') === 'false' && this.windowService.isDesktopRes;
    if (isSecondaryLayer) {
      this.renderer.addClass(this.el.nativeElement, 'secondary-layer');
    }
  }

  ngAfterViewInit() {
    if (this.inputMask && this.inputMask.mask && this.maskedInputRef) {
      const keyupNativeEvent = new Event('keyup');
      setTimeout(_ => {
        // To show input masked on the reload of the page
        this.maskedInputRef.nativeElement.dispatchEvent(keyupNativeEvent);
        // call blur() to prevent input focus on ios devices
        this.maskedInputRef.nativeElement.blur();
      }, 0);
    }
    if (this.form?.value && this.form?.value?.length > 0) {
      this.isInputEmpty = false;
    }
  }

  public focusNativeInput() {
    if (this.inputMask && this.inputMask.mask && this.maskedInputRef) {
      this.maskedInputRef.nativeElement.focus();
    } else if (this.neurotypicalInput) {
      // On ios it is not possible to handle keyboard open and close using capacitor
      // Because that is necessary to use focus() and blur()
      this.neurotypicalInput.nativeElement.focus();
    }
  }

  public hideNativeInput() {
    if (this.inputMask && this.inputMask.mask && this.maskedInputRef) {
      this.maskedInputRef.nativeElement.blur();
    } else if (this.neurotypicalInput) {
      // On ios it is not possible to handle keyboard open and close using capacitor
      // Because that is necessary to use focus() and blur()
      this.neurotypicalInput.nativeElement.blur();
    }
  }

  changeTouch = () => {
    this.touchedState = true;
  }

  keyUpHandle(event) {
    this.isInputEmpty = event.target.value === '';
    if (this.handleUpdatesOnKeyUp) {
      this.form.patchValue(this.value);
      this.form.updateValueAndValidity();
      this.touch();
    }
  }

  getMask(): InputMaskDataModel {
    const masks = {
      phone: {
        maxLenghth: 14,
        mask: '(000) 000-0000',
        additionalCharacter: null
      },
      currencyMask: {
        lazy: true,
        mask: this.currencySymbol ? `${this.currencySymbol}num` : '$num',
        blocks: {
          num: {
            mask: Number,
            scale: 2,
            signed: false,
            thousandsSeparator: ',',
            padFractionalZeros: true,
            normalizeZeros: true,
            radix: '.',
            mapToRadix: ['.']
          }
        }
      },
      zipcode: {
        maxLenghth: 5,
        mask: '00000',
        additionalCharacter: null
      },
      number: {
        lazy: false,
        mask: 'num',
        blocks: {
          num: {
            mask: Number,
          }
        }
      },
      integer: {
        mask: '000000000000000',
        additionalCharacter: null
      }
    };
    return masks[this.mask];
  }

  ngOnChanges(changes: { customMask?: SimpleChange, mask?: SimpleChange, form?: SimpleChange }) {
    if (changes.customMask) {
      this.inputMask = this.customMask;
    }

    if (changes.mask) {
      this.inputMask = this.getMask();
    }

    if (changes.form) {
      const newForm = changes.form.currentValue;
      if (newForm) {
        this.form = newForm;
        this.value = newForm.value;
      }
    }
  }

  setFocused(status) {
    this.focused = status;
    if (!status) {
      this.blur.emit();
    }
  }

  incrementValue(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    const currentValue = parseFloat(this.value) || 0;
    this.value = currentValue + 1;
    this.form?.patchValue(this.value);
    this.form?.updateValueAndValidity();
    this.isInputEmpty = false;
  }

  decrementValue(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    const currentValue = parseFloat(this.value) || 0;
    this.value = currentValue - 1;
    this.form?.patchValue(this.value);
    this.form?.updateValueAndValidity();
    this.isInputEmpty = this.value === '' || this.value === null;
  }

  async executeAction() {
    this.errorMessage = ''
    if (this.loadingState.loading || !this.action) {
      return;
    }

    try {
      this.loadingState.loading = true;
      this.actionLoading = true;
      await this.action();
      if (!this.form.value || this.form.value.length === 0) {
        this.isInputEmpty = true;
      }
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    } finally {
      this.loadingState.loading = false;
      this.actionLoading = false;
    }
  }
}
